/**
 * Auto-scroll behavior for ReasoningChat's message thread: tracks whether
 * the user is scrolled near the bottom, auto-scrolls on new content while
 * they are, and surfaces a "New messages" indicator when they've scrolled
 * away. All the values it reacts to (conversation id, chains, partial
 * steps, conversation state, active task id) are passed in explicitly
 * rather than closed over, so the dependency surface stays visible at the
 * call site.
 */

import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { logger } from '@/lib/logger';

export interface UseChatAutoScrollParams {
  containerRef: RefObject<HTMLDivElement | null>;
  conversationId: string | undefined;
  chains: unknown[];
  partialSteps: unknown[];
  currentConversation: { state?: string } | null;
  currentTaskId: string | null;
}

export interface UseChatAutoScrollResult {
  hasNewContent: boolean;
  scrollToBottom: () => void;
  handleScroll: () => void;
  handleNewMessagesClick: () => void;
  /**
   * Flags "new content" when the user has scrolled away from the bottom.
   * Exposed for callers outside this hook (WebSocket/polling step-progress
   * handlers) that receive streamed content between the effects this hook
   * already watches — same `isNearBottomRef` check those effects use.
   */
  notifyNewContentIfScrolledUp: () => void;
}

export function useChatAutoScroll({
  containerRef,
  conversationId,
  chains,
  partialSteps,
  currentConversation,
  currentTaskId,
}: UseChatAutoScrollParams): UseChatAutoScrollResult {
  // Scroll tracking for smart auto-scroll
  const isNearBottomRef = useRef<boolean>(true);
  const hasNewContentRef = useRef<boolean>(false);
  const isScrollingToBottomRef = useRef<boolean>(false);
  const previousConversationIdRef = useRef<string | undefined>(conversationId);
  const rafPendingRef = useRef<boolean>(false);
  const [hasNewContent, setHasNewContentState] = useState(false);

  // Custom setter that updates both state and ref synchronously to avoid race conditions
  const setHasNewContent = useCallback((value: boolean) => {
    hasNewContentRef.current = value;
    setHasNewContentState(value);
  }, []);

  // Function to scroll to the bottom of the chat to see new AI messages
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      isScrollingToBottomRef.current = true;

      try {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      } catch (error) {
        // Fallback: clear animation flag if scrollTo fails
        isScrollingToBottomRef.current = false;
        logger.error(
          'Scroll animation failed, clearing animation flag',
          error instanceof Error ? error : undefined,
          {
            component: 'ReasoningChat',
            operation: 'scrollToBottom',
          }
        );
        return;
      }

      // Clear the flag after animation completes (~1000ms + buffer)
      // Store timeout ID so we can clear it if user interrupts the scroll

      // Clear any existing timeout before creating new one
      const oldTimeoutId = container.dataset.scrollTimeoutId;
      if (oldTimeoutId) {
        clearTimeout(Number(oldTimeoutId));
        delete container.dataset.scrollTimeoutId;
      }

      const timeoutId = setTimeout(() => {
        isScrollingToBottomRef.current = false;
      }, 1100);

      // Store the timeout ID so handleScroll can clear it if user interrupts
      container.dataset.scrollTimeoutId = String(timeoutId);
    }
  }, [containerRef]);

  const handleScroll = useCallback(() => {
    // Skip if RAF already pending
    if (rafPendingRef.current) {
      return;
    }

    rafPendingRef.current = true;

    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const nearBottom = distanceFromBottom < 100;

      // If we're animating to bottom, check if user interrupted the scroll
      // User interruption = scrolling UP (away from bottom) during animation
      // Programmatic smooth scroll will only trigger scroll events moving DOWN (toward bottom)
      if (isScrollingToBottomRef.current) {
        // If we reached the bottom, animation succeeded - clear flag and timeout
        if (nearBottom) {
          isScrollingToBottomRef.current = false;
          const timeoutId = container.dataset.scrollTimeoutId;
          if (timeoutId) {
            clearTimeout(Number(timeoutId));
            delete container.dataset.scrollTimeoutId;
          }
        }
        // If user scrolled up (away from bottom) during animation, they're interrupting
        // We detect this by checking if scroll position decreased from previous
        else if (
          container.dataset.lastScrollTop &&
          scrollTop < Number(container.dataset.lastScrollTop)
        ) {
          isScrollingToBottomRef.current = false;
          const timeoutId = container.dataset.scrollTimeoutId;
          if (timeoutId) {
            clearTimeout(Number(timeoutId));
            delete container.dataset.scrollTimeoutId;
          }
        }
      }

      // Track scroll position for detecting upward scrolls
      container.dataset.lastScrollTop = String(scrollTop);

      isNearBottomRef.current = nearBottom;

      // Clear "new content" indicator when user scrolls back to bottom
      if (nearBottom && hasNewContentRef.current) {
        setHasNewContent(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally stable: reads from refs only, not state
  }, [containerRef]);

  const handleNewMessagesClick = useCallback(() => {
    setHasNewContent(false);
    scrollToBottom();
    if (containerRef.current) {
      containerRef.current.focus({ preventScroll: true });
    }
  }, [scrollToBottom, setHasNewContent, containerRef]);

  const notifyNewContentIfScrolledUp = useCallback(() => {
    if (!isNearBottomRef.current) {
      setHasNewContent(true);
    }
  }, [setHasNewContent]);

  // Detect conversation switch - ALWAYS scroll to bottom when switching conversations
  useEffect(() => {
    if (
      previousConversationIdRef.current !== conversationId &&
      previousConversationIdRef.current !== undefined
    ) {
      // Reset button state and scroll to bottom on conversation switch
      setHasNewContent(false);
      previousConversationIdRef.current = conversationId;
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
    previousConversationIdRef.current = conversationId;
  }, [conversationId, scrollToBottom, setHasNewContent]);

  // Scroll to bottom when chains change (new messages)
  useEffect(() => {
    if (chains.length > 0) {
      // Respect user scroll position for content updates
      if (!isNearBottomRef.current) {
        setHasNewContent(true);
        return;
      }
      const timer = setTimeout(() => {
        // Re-check scroll position when timeout fires (user may have scrolled during delay)
        // Don't set hasNewContent if we're already scrolling to bottom
        if (isNearBottomRef.current) {
          scrollToBottom();
        } else if (!isScrollingToBottomRef.current) {
          setHasNewContent(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [chains, scrollToBottom, setHasNewContent]);

  // Scroll to bottom when partial steps update
  useEffect(() => {
    if (partialSteps.length > 0) {
      if (!isNearBottomRef.current) {
        setHasNewContent(true);
        return;
      }
      const timer = setTimeout(() => {
        // Re-check scroll position when timeout fires (user may have scrolled during delay)
        // Don't set hasNewContent if we're already scrolling to bottom
        if (isNearBottomRef.current) {
          scrollToBottom();
        } else if (!isScrollingToBottomRef.current) {
          setHasNewContent(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [partialSteps, scrollToBottom, setHasNewContent]);

  // Scroll to bottom when conversation state changes
  useEffect(() => {
    if (currentConversation?.state) {
      if (!isNearBottomRef.current) {
        setHasNewContent(true);
        return;
      }
      const timer = setTimeout(() => {
        // Re-check scroll position when timeout fires (user may have scrolled during delay)
        // Don't set hasNewContent if we're already scrolling to bottom
        if (isNearBottomRef.current) {
          scrollToBottom();
        } else if (!isScrollingToBottomRef.current) {
          setHasNewContent(true);
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [currentConversation?.state, scrollToBottom, setHasNewContent]);

  // Scroll to bottom when currentTaskId changes (new reasoning starts)
  useEffect(() => {
    if (currentTaskId) {
      if (!isNearBottomRef.current) {
        setHasNewContent(true);
        return;
      }
      const timer = setTimeout(() => {
        // Re-check scroll position when timeout fires (user may have scrolled during delay)
        // Don't set hasNewContent if we're already scrolling to bottom
        if (isNearBottomRef.current) {
          scrollToBottom();
        } else if (!isScrollingToBottomRef.current) {
          setHasNewContent(true);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [currentTaskId, scrollToBottom, setHasNewContent]);

  return useMemo(
    () => ({
      hasNewContent,
      scrollToBottom,
      handleScroll,
      handleNewMessagesClick,
      notifyNewContentIfScrolledUp,
    }),
    [
      hasNewContent,
      scrollToBottom,
      handleScroll,
      handleNewMessagesClick,
      notifyNewContentIfScrolledUp,
    ]
  );
}
